import { Controller } from '@hotwired/stimulus'
import { t } from '@lingui/core/macro'

export default class extends Controller {
  static targets = ['container', 'hiddenInput']
  static values = { initialData: String, awsAccounts: Array }

  connect() {
    this.form = this.element.closest('form')
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this))
    }

    this.hiddenInput = this.findHiddenInput()
    if (this.hiddenInput) {
      this.hiddenInput.value = '[]'
    }

    let initialData = []
    if (this.hasInitialDataValue && this.initialDataValue) {
      try {
        const parsed = JSON.parse(this.initialDataValue)
        initialData = Array.isArray(parsed) ? parsed : []
      } catch (_e) {
        initialData = []
      }
    }

    if (initialData.length > 0) {
      initialData.forEach((item) => {
        this.addAccountFields(item)
      })
      this.updateRemoveButtons()
      this.updateHiddenInput()
    }
  }

  findHiddenInput() {
    if (this.hasHiddenInputTarget) {
      return this.hiddenInputTarget
    }
    const form = this.element.closest('form')
    if (form) {
      return form.querySelector('input[name*="account_level_info"]')
    }
    return null
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit.bind(this))
    }
  }

  handleSubmit(_event) {
    this.updateHiddenInput()
  }

  addAccountFields(data = null) {
    const index = this.containerTarget.children.length
    const accountFields = this.createAccountFields(index, data)
    this.containerTarget.appendChild(accountFields)
    this.updateHiddenInput()
    this.updateRemoveButtons()
  }

  removeAccountFields(event) {
    const accountGroup = event.currentTarget.closest('.account-level-info-group')
    const groups = this.containerTarget.querySelectorAll('.account-level-info-group')

    if (groups.length >= 1) {
      const tomSelectElement = accountGroup.querySelector('[data-controller*="tom-select"]')
      if (tomSelectElement) {
        const tomSelectController = this.application.getControllerForElementAndIdentifier(
          tomSelectElement,
          'tom-select',
        )
        if (tomSelectController?.tomSelectInstance) {
          tomSelectController.tomSelectInstance.destroy()
        }
      }
      accountGroup.remove()
      this.updateHiddenInput()
      this.updateRemoveButtons()
    }
  }

  updateRemoveButtons() {
    const removeButtons = this.containerTarget.querySelectorAll('[data-action*="removeAccountFields"]')

    removeButtons.forEach((button) => {
      button.disabled = false
    })
  }

  updateHiddenInput() {
    const accounts = this.collectAccountData()
    const jsonValue = JSON.stringify(accounts)

    if (!this.hiddenInput) {
      this.hiddenInput = this.findHiddenInput()
    }

    if (this.hiddenInput) {
      this.hiddenInput.value = jsonValue
    }
  }

  collectAccountData() {
    const accounts = []
    const groups = this.containerTarget.querySelectorAll('.account-level-info-group')

    groups.forEach((group) => {
      const awsAccountIdInput = group.querySelector('[data-field="aws_account_id"]')
      const baselineCostInput = group.querySelector('[data-field="baseline_cost"]')
      const accountAliasInput = group.querySelector('[data-field="account_alias"]')

      if (!awsAccountIdInput || !baselineCostInput || !accountAliasInput) {
        return
      }

      const awsAccountId = awsAccountIdInput.value.trim()
      const baselineCostValue = baselineCostInput.value.trim()
      let baselineCost = 0
      if (baselineCostValue) {
        const parsed = parseFloat(baselineCostValue)
        baselineCost = Number.isNaN(parsed) ? 0 : parsed
      }
      const accountAlias = accountAliasInput.value.trim()

      if (awsAccountId) {
        accounts.push({
          aws_account_id: awsAccountId,
          baseline_cost: baselineCost,
          account_alias: accountAlias || null,
        })
      }
    })

    return accounts
  }

  createAccountFields(index, data = null) {
    const group = document.createElement('div')
    group.className = 'account-level-info-group mb-3 p-3 border rounded'
    group.dataset.index = index

    const awsAccountId = data?.aws_account_id ? String(data.aws_account_id) : ''
    let baselineCost = ''
    if (data?.baseline_cost !== null && data?.baseline_cost !== undefined) {
      baselineCost = String(data.baseline_cost)
    }
    const accountAlias = data?.account_alias ? String(data.account_alias) : ''

    const accountsOptions = this.awsAccountsValue
      .map(([label, value]) => {
        const selected = value === awsAccountId ? 'selected' : ''
        return `<option value="${this.escapeHtml(value)}" ${selected}>${this.escapeHtml(label)}</option>`
      })
      .join('')

    group.innerHTML = `
      <div class="row">
        <div class="col-md-4">
          <label class="form-label">${t`AWS Account ID`}</label>
          <select
            class="form-select"
            data-field="aws_account_id"
            data-controller="tom-select"
            data-tom-select-placeholder-value="${t`Select account`}">
            <option value="">${t`Select account`}</option>
            ${accountsOptions}
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">${t`Baseline Cost`}</label>
          <input
            type="number"
            class="form-control"
            data-field="baseline_cost"
            value="${this.escapeHtml(baselineCost)}"
            placeholder="0.00"
            step="0.0000001"
            min="0"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label">${t`Account Alias`}</label>
          <input
            type="text"
            class="form-control"
            data-field="account_alias"
            value="${this.escapeHtml(accountAlias)}"
            placeholder="${t`Test Account`}"
          />
        </div>
        <div class="col-md-1 d-flex align-items-end">
          <button
            type="button"
            class="btn btn-outline-danger"
            data-action="click->account-level-info#removeAccountFields"
            title="${t`Remove`}">
            ×
          </button>
        </div>
      </div>
    `

    const inputs = group.querySelectorAll('input[data-field], select[data-field]')
    inputs.forEach((input) => {
      input.addEventListener('input', () => {
        this.updateHiddenInput()
      })
      input.addEventListener('change', () => {
        this.updateHiddenInput()
      })
    })

    return group
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
